import { Component, computed, DestroyRef, effect, ElementRef, inject, OnInit, Renderer2, viewChild } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { NavigationSkipped, NavigationStart, Router } from "@angular/router";
import { CompanyService } from "@core/client/company.service";
import { UserService } from "@core/client/user.service";
import { FeatureFlagsService } from "@core/feature-flags/page-feature-flags.service";
import { Module, navigationItem, NavigationVisibility } from "@core/navigation/navigation.model";
import { NavigationService } from "@core/navigation/navigation.service";
import { UIService } from "@core/ui/ui.service";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { UserImageComponent } from "@identity/components/user-image/user-image.component";
import { FormDisplayControl } from "@oi-soft/common";
import { ThemeModeButtonComponent } from "@shared/components/buttons/theme-mode-button/theme-mode-button.component";
import { CollapseHeaderComponent } from "@shared/components/collapse/collapse-header/collapse-header.component";
import { FormControlComponent } from "@shared/components/form/form-control/form-control.component";
import { CounterDirective } from "@shared/directives/counter/counter.directive";
import * as bootstrap from 'bootstrap';
import { filter } from "rxjs";
import { NavigationListingComponent } from "../navigation-listing/navigation-listing.component";

@Component({
    selector: 'app-navigation-drawer',
    imports: [FontAwesomeModule, FormControlComponent, NavigationListingComponent, ThemeModeButtonComponent, UserImageComponent, CollapseHeaderComponent, CounterDirective],
    templateUrl: './navigation-drawer.component.html',
    styleUrl: './navigation-drawer.component.scss'
})
export class NavigationDrawerComponent implements OnInit {
    
    Modules = Module;
    navigationVisibility = NavigationVisibility;
    bsOffCanvas!:bootstrap.Offcanvas;
    
    renderer = inject(Renderer2);
    destroyRef = inject(DestroyRef);
    router = inject(Router);
    companyService = inject(CompanyService);
    userService = inject(UserService);
    navigationService = inject(NavigationService);
    featureFlagsService = inject(FeatureFlagsService);
    uiService = inject(UIService);
    
    private drawer = viewChild<ElementRef<HTMLElement>>('drawer');
    private searchControl = viewChild<any>('searchControl');
    
    show = this.uiService.navBarOpen;
    selectedIndex = this.uiService.navBarIndex;
    
    search = new FormDisplayControl('Search', '');
    searchQuery = toSignal(this.search.valueChanges);

    data = this.navigationService.navigation;
    
    filteredData = computed<navigationItem[]|undefined>(() => {
        
        if(this.searchQuery()?.toLowerCase()){
            return this.filterNav(this.data());
        }
        else{
            return this.data();
        }
        
    })
    
    selectedModule = computed(() => {
        return this.data().at(this.selectedIndex()!)
    })
    
    constructor(){
        
        effect(() => {
            
            if(this.show()){
                this.bsOffCanvas.show();
            }
            else{
                this.bsOffCanvas.hide();
            }
            
        })

        //Because, when navigation is already opened and another module is clicked, search loses focus.
        effect(() => {
            if(this.uiService.navBarIndex() != undefined){
                this.focusOnSearch();
            }
        })
        
        this.subscribeToNavigation();
        
    }
    
    ngOnInit(): void {
        
        //Setup Bootstrap Offcanvas
        this.bsOffCanvas = new bootstrap.Offcanvas(this.drawer()!.nativeElement);
        
        const showUnlisten = this.renderer.listen(this.drawer()!.nativeElement, 'show.bs.offcanvas', () => {
            this.uiService.setNavBarOpen(true)
        });
        const hideUnlisten = this.renderer.listen(this.drawer()!.nativeElement, 'hide.bs.offcanvas', () => {
            this.uiService.setNavBarOpen(false);
            this.search.reset();
        });
        const shownUnlisten = this.renderer.listen(this.drawer()!.nativeElement, 'shown.bs.offcanvas', () => {
            this.focusOnSearch();
        });

        this.destroyRef.onDestroy(() => {
            showUnlisten();
            hideUnlisten();
            shownUnlisten();
        })
        
    }

    focusOnSearch(){
        this.searchControl()?.input()?.input()?.input()?.nativeElement.focus();
    }
    
    filterNav(data:navigationItem[], inCompany?:boolean): navigationItem[]{
        return data.map(item => {
            
            const itemNavigationFlags = this.featureFlagsService.getNavigationFlag(item.id);
            let newItem;

            if(itemNavigationFlags != undefined && !itemNavigationFlags.enabled){
                return undefined;
            }
            else if((item.company || inCompany) && !this.companyService.company()){
                return undefined;
            }
            else if(item.children && item.children.length > 0){
                newItem = {...item, children: this.filterNav(item.children, inCompany)};
                
                if(newItem.children.length > 0 || newItem.name.toLowerCase().includes(this.searchQuery().toLowerCase())){
                    return newItem;
                }
                else{
                    return undefined;
                }
            }
            else{
                if(item.name.toLowerCase().includes(this.searchQuery().toLowerCase())){
                    return item;
                }
                else{
                    return undefined;
                }
            }
            
        })
        .filter(item => item != undefined)
    }
    
    openNavigationModule(module:navigationItem){
        this.uiService.setNavBarIndex(this.data().findIndex(mod => mod.name == module.name));
        this.search.reset();
    }
    
    subscribeToNavigation(){
        this.router.events
        .pipe(filter((event) => event instanceof NavigationStart || event instanceof NavigationSkipped))
        .subscribe({
            next: () => {
                if(this.show()){
                    this.drawer()!.nativeElement.dispatchEvent(new Event('hide.bs.offcanvas'));
                }
            }
        });
    }

    getModuleGrouped(module:navigationItem):navigationItem[]{

        let moduleGrouped:navigationItem[] = [];

        module.children?.forEach((child) => {
            
            const childGroup = child.group;

            if(childGroup){
                let group = moduleGrouped.find((group) => group.name == child.group);
            
                if(group){
                    group.children?.push(child);
                }
                else{
                    moduleGrouped.push({
                        id: childGroup,
                        name: childGroup,
                        children: [child]
                    });
                }
            }
            else{
                let otherGroup = moduleGrouped.find((group) => group.name == 'Other');
            
                if(otherGroup){
                    otherGroup.children?.push(child);
                }
                else{
                    moduleGrouped.push({
                        id: 'other',
                        name: 'Other',
                        children: [child]
                    });
                }
            }
            
        })

        return moduleGrouped;
    }
}
